import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, FileSpreadsheet, Plus, Save, Trash2, Upload, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RequirePermission from '../../../components/AccessControl/RequirePermission';
import { useStore } from '../../../context/StoreContext';
import { loadServices, servicesFetch, errorText, toNumber, clean, newRowId, readColumn, readSheetFile, writeSheet, writeCsv } from '../pricelistUtils';
import styles from './PricelistAddons.module.css';

// Add-ons of one service (?serviceId=). Services are listed as "Audience › Category › Name".
const makeRow = (a = {}) => ({ id: a._id || a.id || newRowId('addon'), name: a.name || '', duration: a.duration ?? '', price: a.price ?? '' });
const makeRows = (list = []) => { const r = (Array.isArray(list) ? list : []).map(makeRow); return r.length ? r : [makeRow()]; };
const label = (s) => [s.targetDemographic, s.category, s.name].filter(Boolean).join(' › ');
const priceOf = (s) => toNumber(s?.effectivePrice ?? s?.price, 0);

const PricelistAddons = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const { activeStore } = useStore();
    const fileInputRef = useRef(null);
    const storeId = activeStore?.id || activeStore?._id;
    const [services, setServices] = useState([]);
    const [selectedId, setSelectedId] = useState(params.get('serviceId') || '');
    const [rows, setRows] = useState([makeRow()]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [status, setStatus] = useState({ type: '', text: '' });

    const selected = services.find((s) => s.id === selectedId);
    const base = priceOf(selected);
    const valid = rows.filter((r) => clean(r.name));
    const grouped = useMemo(() => {
        const m = new Map();
        services.forEach((s) => { const g = s.targetDemographic || 'Other'; if (!m.has(g)) m.set(g, []); m.get(g).push(s); });
        return [...m.entries()];
    }, [services]);

    const fetchServices = useCallback(async () => {
        if (!storeId || storeId === 'global_hq') { setIsLoading(false); return; }
        setIsLoading(true);
        try { setServices(await loadServices(storeId)); setStatus({ type: '', text: '' }); } catch (e) { setStatus({ type: 'error', text: errorText(e) }); } finally { setIsLoading(false); }
    }, [storeId]);
    useEffect(() => { fetchServices(); }, [fetchServices]);

    // keep the chosen service in the address so the page can be reopened
    useEffect(() => {
        if (!services.length) return;
        const wanted = params.get('serviceId');
        const next = services.some((s) => s.id === wanted) ? wanted : services.some((s) => s.id === selectedId) ? selectedId : services[0].id;
        if (next !== selectedId) setSelectedId(next);
        if (next !== wanted) setParams({ serviceId: next }, { replace: true });
    }, [services, params, selectedId, setParams]);
    useEffect(() => { if (selected) setRows(makeRows(selected.addons)); }, [selected?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

    const choose = (id) => { setSelectedId(id); setParams({ serviceId: id }); setStatus({ type: '', text: '' }); };
    const setField = (id, f, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [f]: v } : r)));
    const remove = (id) => setRows((rs) => { const n = rs.filter((r) => r.id !== id); return n.length ? n : [makeRow()]; });

    const importFile = async (file) => {
        if (!file) return;
        setIsImporting(true);
        try {
            const byName = new Map();
            (await readSheetFile(file)).forEach((src) => {
                const name = clean(readColumn(src, ['add-on name', 'addon name', 'add on', 'name']));
                if (!name) return;
                const existing = rows.find((r) => clean(r.name).toLowerCase() === name.toLowerCase());
                byName.set(name.toLowerCase(), {
                    id: existing?.id || newRowId('import'), name,
                    duration: Math.max(0, toNumber(readColumn(src, ['duration', 'duration mins', 'minutes', 'mins']), 0)),
                    price: Math.max(0, toNumber(readColumn(src, ['add-on price', 'addon price', 'price', 'amount', 'rate']), 0)),
                });
            });
            const imported = [...byName.values()];
            if (!imported.length) throw new Error('No add-ons found. Use Add-on Name, Duration and Price columns.');
            setRows(imported);
            setStatus({ type: 'success', text: `${imported.length} add-ons loaded for ${label(selected)}. Check them, then Save.` });
        } catch (e) { setStatus({ type: 'error', text: errorText(e) }); } finally { setIsImporting(false); }
    };

    const exportRows = () => valid.map((r) => ({ 'Add-on Name': clean(r.name), 'Duration (mins)': toNumber(r.duration), 'Add-on Price': toNumber(r.price), 'Combo Price': base + toNumber(r.price) }));
    const fileBase = (selected ? label(selected) : 'Addons').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '');

    const save = async () => {
        if (!selected) return;
        const names = valid.map((r) => clean(r.name).toLowerCase());
        const dup = names.find((n, i) => names.indexOf(n) !== i);
        if (dup) { setStatus({ type: 'error', text: `"${dup}" is listed twice.` }); return; }
        if (valid.some((r) => r.price === '')) { setStatus({ type: 'error', text: 'Every add-on needs a price.' }); return; }
        setIsSaving(true);
        try {
            const addons = valid.map((r) => ({ ...(String(r.id).startsWith('addon-') || String(r.id).startsWith('import-') ? {} : { id: r.id }), name: clean(r.name), duration: Math.max(0, Math.round(toNumber(r.duration))), price: Math.max(0, toNumber(r.price)) }));
            const json = await servicesFetch(`/${selected.id}`, { method: 'PUT', storeId, body: { addons } });
            if (!json.success) throw new Error(json.message);
            setServices((list) => list.map((s) => (s.id === selected.id ? { ...s, ...json.data, id: s.id } : s)));
            setRows(makeRows(json.data?.addons || addons));
            setStatus({ type: 'success', text: `Add-ons saved for ${label(selected)}.` });
        } catch (e) { setStatus({ type: 'error', text: errorText(e) }); } finally { setIsSaving(false); }
    };

    return (
        <div className={styles.pageWrapper}>
            <header className={styles.pageHeader}>
                <div className={styles.headerIdentity}>
                    <button type="button" className={styles.backBtn} onClick={() => navigate('/pricelist')} aria-label="Back to price list"><ArrowLeft size={16} /></button>
                    <div>
                        <h1 className={styles.pageTitle}>Add-ons</h1>
                        <p className={styles.pageSubtitle}>Extras for one service. Type them or upload Excel / CSV.</p>
                    </div>
                </div>
                <div className={styles.headerRight}>
                    <div className={styles.serviceContext}>
                        <label htmlFor="addon-service">Service</label>
                        <select id="addon-service" value={selectedId} onChange={(e) => choose(e.target.value)} disabled={isLoading || !services.length}>
                            {!services.length && <option value="">{isLoading ? 'Loading services...' : 'No services found'}</option>}
                            {grouped.map(([aud, list]) => (
                                <optgroup key={aud} label={aud}>
                                    {list.map((s) => <option key={s.id} value={s.id}>{s.category} › {s.name}</option>)}
                                </optgroup>
                            ))}
                        </select>
                    </div>
                    <button type="button" className={styles.cancelBtn} onClick={() => navigate('/pricelist')}><X size={13} /> Cancel</button>
                    <RequirePermission module="pricelist" action="manage_addons">
                        <button type="button" className={styles.saveBtn} onClick={save} disabled={!selected || isSaving}><Save size={13} /> {isSaving ? 'Saving...' : `Save ${valid.length} add-ons`}</button>
                    </RequirePermission>
                </div>
            </header>

            {status.text && <div className={`${styles.statusBar} ${status.type === 'error' ? styles.statusError : styles.statusSuccess}`}>{status.text}</div>}

            <main className={styles.workspace}>
                <section className={styles.gridPanel}>
                    <div className={styles.panelHeader}>
                        <strong>{selected ? label(selected) : 'Review & edit'}{selected && <small style={{ fontWeight: 700, opacity: 0.6 }}> · base ₹{base}</small>}</strong>
                        <RequirePermission module="pricelist" action="manage_addons">
                            <button type="button" className={styles.addRowBtn} onClick={() => setRows((rs) => [...rs, makeRow()])} disabled={!selected}><Plus size={13} /> Add row</button>
                        </RequirePermission>
                    </div>
                    <div className={styles.tableScroll}>
                        <table className={styles.addonTable}>
                            <thead>
                                <tr><th className={styles.numberCol}>#</th><th>Add-on name<span>*</span></th><th className={styles.durationCol}>Duration <small>(mins)</small></th><th className={styles.priceCol}>Price</th><th className={styles.comboCol}>Combo price</th><th className={styles.actionCol}> </th></tr>
                            </thead>
                            <tbody>
                                {isLoading ? <tr><td colSpan="6" className={styles.tableMessage}>Loading services...</td></tr>
                                    : !selected ? <tr><td colSpan="6" className={styles.tableMessage}>Choose a service to manage its add-ons.</td></tr>
                                        : rows.map((r, i) => (
                                            <tr key={r.id}>
                                                <td className={styles.rowNumber}>{i + 1}</td>
                                                <td><input className={styles.cellInput} value={r.name} onChange={(e) => setField(r.id, 'name', e.target.value)} placeholder="Required" /></td>
                                                <td><input className={`${styles.cellInput} ${styles.numericInput}`} type="number" min="0" value={r.duration} onChange={(e) => setField(r.id, 'duration', e.target.value)} placeholder="0" /></td>
                                                <td><div className={styles.currencyInput}><span>₹</span><input className={`${styles.cellInput} ${styles.numericInput}`} type="number" min="0" value={r.price} onChange={(e) => setField(r.id, 'price', e.target.value)} placeholder="0" /></div></td>
                                                <td className={styles.comboValue}>₹{(base + toNumber(r.price)).toFixed(2)}</td>
                                                <td className={styles.actionCell}><button type="button" className={styles.deleteBtn} onClick={() => remove(r.id)} title="Remove row"><Trash2 size={13} /></button></td>
                                            </tr>
                                        ))}
                            </tbody>
                        </table>
                    </div>
                    <div className={styles.gridFooter}><span>{valid.length} add-on{valid.length === 1 ? '' : 's'} ready to save</span><span>Combo = service final price + add-on</span></div>
                </section>

                <aside className={styles.uploadPanel}>
                    <h2>Excel / CSV upload</h2>
                    <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; importFile(f); }} hidden />
                    <RequirePermission module="pricelist" action="manage_addons">
                        <button type="button" className={styles.dropZone} onClick={() => fileInputRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); importFile(e.dataTransfer.files?.[0]); }} disabled={!selected || isImporting}>
                            <Upload size={27} />
                            <strong>{isImporting ? 'Reading file...' : 'Upload Excel / CSV'}</strong>
                            <span>Drag and drop or click to choose</span>
                        </button>
                    </RequirePermission>
                    <button type="button" className={styles.sampleCard} onClick={() => writeSheet([{ 'Add-on Name': 'Hair Wash', 'Duration (mins)': 10, 'Add-on Price': 100 }, { 'Add-on Name': 'Head Massage', 'Duration (mins)': 15, 'Add-on Price': 200 }], 'Addon_Template.xlsx', 'Add-ons')}>
                        <FileSpreadsheet size={16} />
                        <span><strong>Need the format?</strong><small>Download the sample</small></span>
                    </button>
                    <div className={styles.warningBox}>
                        <AlertCircle size={15} />
                        <p>The file replaces the add-ons in the grid for this service only. Nothing is saved until you press Save. For many services at once, use the Master editor.</p>
                    </div>
                    <RequirePermission module="pricelist" action="export">
                        <div className={styles.uploadBottom}>
                            <span>Download</span>
                            <div>
                                <button type="button" onClick={() => writeCsv(exportRows(), `Addons_${fileBase}.csv`)} disabled={!valid.length}>CSV</button>
                                <button type="button" onClick={() => writeSheet(exportRows(), `Addons_${fileBase}.xlsx`, 'Add-ons')} disabled={!valid.length}>Excel</button>
                            </div>
                        </div>
                    </RequirePermission>
                </aside>
            </main>
        </div>
    );
};

export default PricelistAddons;
